import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  PlusOutlined,
  EditOutlined,
  AppstoreOutlined,
  DragOutlined,
} from "@ant-design/icons";
import { Button, Card } from "antd";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Add from "./Add";
import Edit from "./Edit";
import { saveLayout, getLayout } from "../../utils/categoryLayoutDB";
import {
  CATEGORY_SIDEBAR_KEY,
  CATEGORY_SIDEBAR_MIN_WIDTH,
  CATEGORY_SIDEBAR_MAX_WIDTH,
  CATEGORY_SIDEBAR_DEFAULT_WIDTH,
  DEFAULT_CATEGORY,
} from "../../utils/constants";

const Categories = ({
  categories = [],
  setCategories,
  selectedCategory,
  setSelectedCategory,
}) => {
  const { t } = useTranslation("pages");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [width, setWidth] = useState(CATEGORY_SIDEBAR_DEFAULT_WIDTH);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [savedOrderIds, setSavedOrderIds] = useState(null);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const currentWidthRef = useRef(280);
  const dragSnapshotRef = useRef(null);
  const useSnapshotRef = useRef(false);
  const [snapshotKey, setSnapshotKey] = useState(0);

  /* ---------- Restore layout ---------- */
  useEffect(() => {
    (async () => {
      const saved = await getLayout(CATEGORY_SIDEBAR_KEY);
      if (saved?.width) {
        setWidth(saved.width);
        currentWidthRef.current = saved.width;
      }
      // IMPORTANT: don't overwrite fetched categories with stale objects from IndexedDB.
      // Persist only the order (ids) and merge it with the latest categories from the API.
      if (Array.isArray(saved?.orderIds)) setSavedOrderIds(saved.orderIds);
    })();
  }, []);

  // Keep width ref in sync with state
  useEffect(() => {
    currentWidthRef.current = width;
  }, [width]);

  const orderedCategories = useMemo(() => {
    // During drag and animation, use the frozen snapshot to prevent visual jumps
    if (useSnapshotRef.current && dragSnapshotRef.current) {
      return dragSnapshotRef.current;
    }

    if (!Array.isArray(categories)) return [];
    if (!Array.isArray(savedOrderIds) || savedOrderIds.length === 0) return categories;

    const byId = new Map(categories.map((c) => [c?._id, c]));
    const ordered = [];

    // First: ids we know about, in saved order
    for (const id of savedOrderIds) {
      const item = byId.get(id);
      if (item) ordered.push(item);
    }
    // Then: any new categories not present in saved order
    for (const c of categories) {
      if (c?._id && !savedOrderIds.includes(c._id)) ordered.push(c);
    }
    return ordered;
  }, [categories, savedOrderIds, snapshotKey]);

  /* ---------- Drag reorder ---------- */
  const onDragStart = () => {
    draggingRef.current = true;
    setIsDragging(true);
    // Freeze the current order during drag to prevent re-renders
    dragSnapshotRef.current = [...orderedCategories];
    useSnapshotRef.current = true;
  };

  const onDragEnd = async (result) => {
    if (!result.destination) {
      draggingRef.current = false;
      setIsDragging(false);
      dragSnapshotRef.current = null;
      useSnapshotRef.current = false;
      return;
    }

    // Use the snapshot to ensure we're working with the order at drag start
    const items = dragSnapshotRef.current ? [...dragSnapshotRef.current] : [...orderedCategories];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    // Update state immediately - react-beautiful-dnd handles the visual animation
    setCategories(items);
    const orderIds = items.map((c) => c._id).filter(Boolean);
    setSavedOrderIds(orderIds);
    
    // Clear dragging state and snapshot after animation completes
    // This prevents the visual jump by keeping the snapshot until animation finishes
    setTimeout(() => {
      draggingRef.current = false;
      setIsDragging(false);
      useSnapshotRef.current = false;
      dragSnapshotRef.current = null;
      setSnapshotKey(prev => prev + 1); // Force memo recalculation
      saveLayout(CATEGORY_SIDEBAR_KEY, { width, orderIds });
    }, 150);
  };

  /* ---------- Resize logic ---------- */
  const handleMouseMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    // Resizing from right edge (drag right = wider, drag left = narrower)
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(CATEGORY_SIDEBAR_MIN_WIDTH, Math.min(CATEGORY_SIDEBAR_MAX_WIDTH, startWidthRef.current + diff));
    setWidth(newWidth);
    currentWidthRef.current = newWidth;
  }, []);

  const handleMouseUp = useCallback(async () => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    
    // Save layout after resize ends using the current width from ref
    await saveLayout(CATEGORY_SIDEBAR_KEY, {
      width: currentWidthRef.current,
      orderIds: orderedCategories.map((c) => c._id).filter(Boolean),
    });
  }, [handleMouseMove, orderedCategories]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [width, handleMouseMove, handleMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [handleMouseMove, handleMouseUp]);

  /* ---------- Select / Deselect ---------- */
  const toggleCategory = (value) => {
    if (draggingRef.current) return;
    // Never set to null: `Products` expects "All" to show everything.
    setSelectedCategory((prev) => (prev === value ? DEFAULT_CATEGORY : value));
  };

  return (
    <div
      className="relative h-full bg-white border-r border-gray-200 flex flex-col pr-3"
      style={{ width }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b font-medium text-gray-700 text-sm">
        <AppstoreOutlined />
        {t("categories")}
      </div>

      {/* Actions */}
      <div className="p-3 space-y-2">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          block
          onClick={() => setIsAddModalOpen(true)}
        >
          {t("addCategory")}
        </Button>

        <Button
          icon={<EditOutlined />}
          block
          onClick={() => setIsEditModalOpen(true)}
        >
          {t("editCategories")}
        </Button>
      </div>

      {/* Category List */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        <DragDropContext
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <Droppable droppableId="categories">
            {(provided) => (
              <div 
                ref={provided.innerRef} 
                {...provided.droppableProps} 
                className="space-y-2"
                style={{ 
                  minHeight: '1px',
                  ...provided.droppableProps.style 
                }}
              >
                {/* ALL */}
                <Card
                  size="small"
                  onClick={() => toggleCategory("All")}
                  className={`cursor-pointer relative pr-8 select-none ${
                    !isDragging ? "transition-all duration-200" : ""
                  }
                    ${
                      selectedCategory === DEFAULT_CATEGORY
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : !isDragging ? "hover:shadow-md hover:-translate-y-[1px] hover:border-gray-300" : ""
                    }`}
                >
                  {t("all")}
                </Card>

                {orderedCategories.map((item, index) => (
                  <Draggable
                    key={item._id}
                    draggableId={item._id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Card
                        size="small"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        onClick={() => toggleCategory(item.title)}
                        style={{
                          ...provided.draggableProps.style,
                          willChange: snapshot.isDragging ? 'transform' : 'auto',
                        }}
                        className={`cursor-pointer relative pr-8 select-none ${
                          !isDragging && !snapshot.isDragging ? "transition-all duration-200" : ""
                        }
                          ${
                            selectedCategory === item.title
                              ? "border-blue-500 bg-blue-50 shadow-sm"
                              : !isDragging && !snapshot.isDragging ? "hover:shadow-md hover:-translate-y-[1px] hover:border-gray-300" : ""
                          }`}
                      >
                        {/* Title */}
                        <span className="block truncate select-none">{item.title}</span>

                        {/* Drag Handle (Top Right) */}
                        <span
                          {...provided.dragHandleProps}
                          className={`absolute top-2 right-2 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing ${
                            !isDragging ? "transition-colors" : ""
                          }`}
                        >
                          <DragOutlined />
                        </span>
                      </Card>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Elegant resize handle - positioned on right edge */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute top-0 bottom-0 cursor-col-resize z-50 transition-colors duration-200"
        style={{ 
          touchAction: "none",
          right: "0px",
          width: "5px",
          pointerEvents: "auto",
          backgroundColor: isResizing ? "rgba(59, 130, 246, 0.15)" : "transparent"
        }}
        onMouseEnter={(e) => {
          if (!isResizing) {
            e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.1)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isResizing) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
        title={t("dragToResize")}
      >
        {/* Elegant three-dot grip indicator */}
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 transition-all duration-200"
          style={{
            opacity: isResizing ? 1 : 0.4
          }}
        >
          <div 
            className="w-1.5 h-1.5 rounded-full transition-all duration-200 shadow-sm"
            style={{
              backgroundColor: isResizing ? "#3b82f6" : "#94a3b8",
              boxShadow: isResizing ? "0 0 4px rgba(59, 130, 246, 0.4)" : "none"
            }}
          />
          <div 
            className="w-1.5 h-1.5 rounded-full transition-all duration-200 shadow-sm"
            style={{
              backgroundColor: isResizing ? "#3b82f6" : "#94a3b8",
              boxShadow: isResizing ? "0 0 4px rgba(59, 130, 246, 0.4)" : "none"
            }}
          />
          <div 
            className="w-1.5 h-1.5 rounded-full transition-all duration-200 shadow-sm"
            style={{
              backgroundColor: isResizing ? "#3b82f6" : "#94a3b8",
              boxShadow: isResizing ? "0 0 4px rgba(59, 130, 246, 0.4)" : "none"
            }}
          />
        </div>
      </div>

      {/* Modals */}
      {isAddModalOpen && (
        <Add
          isAddModalOpen={isAddModalOpen}
          setIsAddModalOpen={setIsAddModalOpen}
          categories={categories}
          setCategories={setCategories}
        />
      )}

      {isEditModalOpen && (
        <Edit
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          categories={categories}
          setCategories={setCategories}
          setCategoryTitle={setSelectedCategory}
        />
      )}
    </div>
  );
};

export default Categories;
